/**
 * @author Mathieu Chantot <mathieu.chantot@hes-so.ch>
 * @date 01/24
 */

import {point, midpoint, distance, bearing, destination, pointToLineDistance, along, lineString, sector, inside, coordAll, getCoord} from '@turf/turf';
import {Bezier} from "bezier-js";
import {Smooth} from './Smooth.js';

const turf = {point, midpoint, distance, bearing, destination, pointToLineDistance, along, lineString, sector, inside, coordAll, getCoord};
/**
 * This class is used to compute the trajectory that will be sent to the vehicle.
 */
export default class PathComputing{
	/**
	 * @param {number} maxPointDistance - Maximum distance between two points in meters.
	 * @param {object} vehiclePos - Position of the vehicle.
	 * @property {number} vehiclePos.lng - Longitude of the vehicle.
	 * @property {number} vehiclePos.lat - Latitude of the vehicle.
	 * @property {number} vehiclePos.heading - Angle of the vehicle in degrees.
	 * @param {number} circleRadius - Radius of the circle around the vehicle in meters.
	 * @param {number} maxStartingAngle - Maximum angle between the vehicle and the first point out of the circle in degrees.
	 * @param {number} TurningRadius - Turning radius of the vehicle in meters.
	 * @param {number} SmoothDistance - Distance used to smooth the trajectory.
	 */
	constructor(maxPointDistance,  vehiclePos, circleRadius, maxStartingAngle, turningRadius, SmoothDistance){
		this.maxPointDistance = maxPointDistance;
		this.vehiclePos = vehiclePos;
		this.circleRadius = circleRadius;
		this.maxStartingAngle = maxStartingAngle;
		this.turningRadius = turningRadius;
		this.SmoothDistance = SmoothDistance;
		this.comment = [];
		this.startInside = true;
	}

	/**
	 * Main funtion for trajectory computing, it checks if the trajectory is valid and if it is, complete and smooth it.
	 * @param {Object[]} trajectory - User-defined trajectory.
	 * @property {number} trajectory[].x - The x-coordinate of the point.
	 * @property {number} trajectory[].y - The y-coordinate of the point.
	 * @returns {boolean|Object[]} - If valid, Computed trajectory
	 * @property {boolean} trajectory[].x - The x-coordinate of the point.
	 */
	computeTrajectory(trajectory){
		let computedTrajectory = [];
		this.addPoints(trajectory);
		this.checkStart(trajectory)
		let filter = new Smooth(Math.round(this.SmoothDistance /(this.maxPointDistance*0.75)));
		trajectory = filter.filter(trajectory)
		if(this.startInside){
			let bezier = this.bezierCurve(trajectory);
			computedTrajectory.push(...bezier.map(point => [point.x, point.y]));
			console.log('startInside');
		}
		this.addTrajectory(computedTrajectory, trajectory);
		this.turningRadiusControl(computedTrajectory)
		var Pathlimits = this.vehiclesSize(computedTrajectory);
		return [computedTrajectory, Pathlimits, this.comment];
	}

	/**
	 * Check if each trajectory angle is under the maximum turning radius.
	 * @param {object[]} trajectory 
	 * @returns 
	 */
	turningRadiusControl(trajectory){
		const maxAngle = 180/(Math.PI*this.turningRadius);
		console.log("maxAngle :", maxAngle);
		//console.log(trajectory.length )
		for(let i = 0; i < trajectory.length - 2; i++){
			let angle = Math.abs(turf.bearing(trajectory[i], trajectory[i+1])- turf.bearing(trajectory[i+1], trajectory[i+2]));
			let distance = (turf.distance(trajectory[i], trajectory[i+1]) + turf.distance(trajectory[i+1], trajectory[i+2]))*1000/2;
			/**Remove this log when everything is tested */
			if(angle > 180){
				angle = 360 - angle;
			}
			/*if(angle/distance > 50){
				console.log("angle/distance :", angle/distance,"angle", angle, "distance :", distance);
			}*/
			if(angle/distance > maxAngle ){
				console.log("At least one corner is too sharp");
				this.comment.push("At least one corner is too sharp");
				return false;
			}
		}
		return true;
	}

	/**
	 * Build a limit line corresponding to the vehicle size on both side of the trajectory.
	 * @param {object[]} trajectory 
	 * @returns {object[]} - Right and left limit lines.
	 */
	vehiclesSize(trajectory){
		let rightLimit = [];
		let leftLimit = [];
		let TotalLimit = [];
		for (let i = 0; i < trajectory.length-1; i++){
			let bearing = turf.bearing(trajectory[i], trajectory[i+1]);
			let right = turf.getCoord(turf.destination(trajectory[i], 0.001, bearing + 90));
			if(turf.pointToLineDistance(right, trajectory)>=0.00099){
				rightLimit.push(right);
			}else if(rightLimit.length > 1){
					TotalLimit.push(rightLimit);
					rightLimit = [];
			}
			
			let left = turf.getCoord(turf.destination(trajectory[i], 0.001, bearing - 90));
			if(turf.pointToLineDistance(left, trajectory)>= 0.00099){
				leftLimit.push(left);
			}else if(leftLimit.length > 1){
				TotalLimit.push(leftLimit);
				leftLimit = [];
			}
		}
		TotalLimit.push(leftLimit);
		TotalLimit.push(rightLimit);
		return TotalLimit;
	}

	/**
	 * Add the user-defined trajectory to the computed trajectory start.
	 * @param {object[]} trajectory 
	 */
	addTrajectory(computedTrajectory, trajectory){
		if(computedTrajectory.length !== 0){
			let computedTrajectoryDist = turf.distance(computedTrajectory[computedTrajectory.length - 1], [this.vehiclePos.lng, this.vehiclePos.lat]);
			while(computedTrajectoryDist >= turf.distance(trajectory[0],[this.vehiclePos.lng, this.vehiclePos.lat])){
				trajectory.shift();
				if (trajectory.length === 0){
					break;
				}
			}
		}
		computedTrajectory.push(...trajectory);
	}

	/**
	 * Build a bezier curve from the vehicle to the trajectory out of the circle.
	 * Using four points: 
	 * 	- The vehicle position.
	 * 	- A point one meter in front of the vehicle.
	 *  - The first trajectory point out of the circle.
	 * 	- A point following the trajectory on one meter.
	 * @param {object[]} trajectory
	 * @returns {object[]} - Bezier curve from vehicle to the trajectory out of the circle.
	 */
	bezierCurve(trajectory){
		let points = [];
		points = points.concat([this.vehiclePos.lng, this.vehiclePos.lat])
		points = points.concat(turf.getCoord(turf.destination([this.vehiclePos.lng, this.vehiclePos.lat], 0.001, this.vehiclePos.heading)));
		points = points.concat(trajectory[0]);
		points = points.concat(turf.getCoord(turf.along(turf.lineString(trajectory),0.001).geometry.coordinates));
		let curve = new Bezier(points);
		//let curve = new Bezier();
		let bezier = curve.getLUT();
		return bezier;
	}

	/**
	 * Add points to the trajectory while the distance between two points is too big.
	 * @param {object[]} trajectory
	 */
	addPoints(trajectory){
		let i = 0;
		while(i < trajectory.length -1){
			let point = trajectory[i];
			let nextPoint = trajectory[i+1];
			if(this.maxPointDistance <= turf.distance(point, nextPoint)*1000){
				trajectory.splice(i+1, 0, turf.getCoord(turf.midpoint(turf.point(point), turf.point(nextPoint))));
			}else{
				i++;
			}
		}
	}

	/**
	 * Check if the user defined trajectory is a valid drawing.
	 * @param {object[]} trajectory
	 * @returns {boolean} - If the trajectory is valid drawing.
	 */
	checkStart(trajectory){
		console.log("checkStart")
		const options = {steps: 50, units: 'kilometers', properties: {fill: '#0ff'}};
		let sector1 = turf.sector([this.vehiclePos.lng, this.vehiclePos.lat], this.circleRadius + 0.002 , this.vehiclePos.heading - this.maxStartingAngle, this.vehiclePos.heading + this.maxStartingAngle, options)
		let sector2 = turf.sector([this.vehiclePos.lng, this.vehiclePos.lat], this.circleRadius + 0.002 , (this.vehiclePos.heading + 180)%360 - this.maxStartingAngle, (this.vehiclePos.heading + 180)%360 + this.maxStartingAngle, options)
		let insideRadius = true
		if(turf.distance(trajectory[0], [this.vehiclePos.lng, this.vehiclePos.lat])*1000 > this.circleRadius){
			this.startInside = false;
		}
		let i = 0;
		while( i < trajectory.length && insideRadius){
			if(turf.distance(trajectory[i], [this.vehiclePos.lng, this.vehiclePos.lat])*1000 <= this.circleRadius){
				trajectory.splice(i,1);
			}else{
				insideRadius = false;
			}
		}
		if (insideRadius){
			console.log("The path does not go out of the circle");
			this.comment.push("The path does not go out of the circle.");
		}

		console.log("startInside", this.startInside);
	
		if(this.startInside && !turf.inside(trajectory[0], sector1) && !turf.inside(trajectory[0], sector2)){
			console.log("The path does not cross the arc in the sector");
			this.comment.push("The path does not cross the arc in the sector.");
		}
		if(turf.inside(trajectory[0], sector2)){
			this.vehiclePos.heading = (180 + this.vehiclePos.heading)%360;
		}
	}
}